import { useCallback } from 'react';
import { Category } from 'shared/types/product';
import { ProductQuery } from 'shared/types/query/ProductQuery';
import { ProductPaginatedResult } from 'shared/types/result/PaginatedResult';
import useSWR from 'swr';
import { sdk } from 'sdk';
import { Product } from 'types/entity/product';

const useProduct = ({ query, limit }: Partial<ProductQuery> = {}) => {
  const { data: productsData } = useSWR(['/action/product/query', query, limit], () =>
    sdk.composableCommerce.product.query({ query, limit }),
  );

  const products = (productsData?.isError ? [] : (productsData?.data?.items as Product[])) ?? [];

  const { data: categoriesData } = useSWR('/action/product/queryCategories', () =>
    sdk.callAction<ProductPaginatedResult>({
      actionName: 'product/queryCategories',
      query: { format: 'tree' },
    }),
  );

  const categories = (categoriesData?.isError ? [] : (categoriesData?.data?.items as Category[])) ?? [];

  const queryProducts = useCallback(async (productQuery: ProductQuery) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.query(productQuery);

    return res;
  }, []);

  const queryRecentlyViewedProduct = useCallback(async (sku: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.getRecentlyViewedProduct(sku);

    return res;
  }, []);

  const queryProductSupplies = useCallback(async (supplies: string[]) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.getProductSupplies(supplies);
    if (res.isError) throw new Error(res?.error?.message || 'Something went wrong');
    return res.data;
  }, []);

  const getAvailabilityMessage = useCallback(async (sku: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.getAvailabilityMessage(sku);
    return res;
  }, []);

  const getShopCollection = useCallback(async (categoryId: string, sku: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.getShopCollection({
      categoriesIds: [categoryId],
      exclude: [{ field: 'key', value: sku }],
    });
    return res;
  }, []);

  const shareProductLink = useCallback(async (email: string, pdpShareLink: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.shareProductLink({
      email,
      pdpShareLink,
    });
    return res;
  }, []);

  return {
    products,
    categories,
    query: queryProducts,
    queryRecentlyViewedProduct,
    getAvailabilityMessage,
    getShopCollection,
    queryProductSupplies,
    shareProductLink,
  };
};

export default useProduct;
